import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import TextareaAutosize from 'react-textarea-autosize';

import { Button } from 'components/ui/button';
import { Card, CardContent } from 'components/ui/card';
import { Input } from 'components/ui/input';
import { ScrollArea } from 'components/ui/scroll-area';
import { useAuth } from 'contexts/AuthContext';
import { useToolkitNotes } from 'hooks/useToolkitNotes';
import { ToolkitNote } from 'types';

import { differenceInCalendarDays, format, isSameYear, startOfDay } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { Loader2, PlusIcon, SearchIcon, Trash2Icon } from 'lucide-react';

interface ToolkitProps {}

interface NoteGroup {
  label: string;
  notes: ToolkitNote[];
}

interface ToolkitItemProps {
  note: ToolkitNote;
  isActive: boolean;
  timezone: string;
  onSelect: (id: string) => void;
}

interface ToolkitMainSectionProps {
  note: ToolkitNote | null;
  editorState: { title: string; body: string };
  timezone: string;
  isSaving: boolean;
  onTitleChange: (value: string) => void;
  onBodyChange: (value: string) => void;
  onDelete: () => void;
  onCreate: () => void;
}

const DEFAULT_AUTOSAVE_DELAY = 600;
const UNTITLED_NOTE = 'Untitled note';

// Saves are serialized per note across Toolkit mounts, so an older save can never land after a newer one.
const noteSaveQueues = new Map<string, Promise<void>>();
// Saves still queued for a successfully deleted note are skipped.
const deletedNoteIds = new Set<string>();

const editableTitle = (title: string) => title === UNTITLED_NOTE ? '' : title;

const sortByUpdated = (notes: ToolkitNote[]) => {
  return [...notes].sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime());
};

const formatListDate = (note: ToolkitNote, timezone: string) => {
  const zoned = toZonedTime(new Date(note.updated_at), timezone);
  return format(zoned, 'EEEE, MMM d');
};

export const Toolkit: React.FC<ToolkitProps> = () => {
  const { user } = useAuth();
  const timezone =
    user?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

  const {
    notes,
    loading,
    error,
    selectedNoteId,
    selectNote,
    createNote,
    updateNote,
    deleteNote,
  } = useToolkitNotes();

  const [searchTerm, setSearchTerm] = useState('');
  const [editorState, setEditorState] = useState({ title: '', body: '' });
  const [isSaving, setIsSaving] = useState(false);

  const pendingSaveRef = useRef<{
    noteId: string;
    updates: Partial<Pick<ToolkitNote, 'title' | 'body'>>;
  } | null>(null);
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const seededNoteIdRef = useRef<string | null>(null);
  // Unsaved or in-flight editor text per note, so reselecting a note mid-save shows what was typed.
  const localDraftsRef = useRef<Record<string, { title: string; body: string; revision: number }>>({});
  const editorStateRef = useRef({ title: '', body: '' });
  editorStateRef.current = editorState;

  const filteredNotes = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) {
      return sortByUpdated(notes);
    }

    return sortByUpdated(
      notes.filter((note) => {
        const haystack = `${note.title} ${note.body}`.toLowerCase();
        return haystack.includes(term);
      }),
    );
  }, [notes, searchTerm]);

  const groupedNotes = useMemo<NoteGroup[]>(() => {
    if (!filteredNotes.length) {
      return [];
    }

    const now = toZonedTime(new Date(), timezone);
    const todayStart = startOfDay(now);

    const today: ToolkitNote[] = [];
    const previous7: ToolkitNote[] = [];
    const monthBuckets = new Map<number, { label: string; notes: ToolkitNote[] }>();
    const pastYears = new Map<number, ToolkitNote[]>();

    filteredNotes.forEach((note) => {
      const zoned = toZonedTime(new Date(note.updated_at), timezone);
      const noteDay = startOfDay(zoned);
      const dayDiff = differenceInCalendarDays(todayStart, noteDay);

      if (dayDiff <= 0) {
        today.push(note);
        return;
      }

      if (dayDiff <= 7) {
        previous7.push(note);
        return;
      }

      if (isSameYear(zoned, now)) {
        const monthKey = zoned.getMonth();
        const existing = monthBuckets.get(monthKey);
        if (existing) {
          existing.notes.push(note);
        } else {
          monthBuckets.set(monthKey, {
            label: format(zoned, 'LLLL'),
            notes: [note],
          });
        }
        return;
      }

      const year = zoned.getFullYear();
      const bucket = pastYears.get(year) ?? [];
      bucket.push(note);
      pastYears.set(year, bucket);
    });

    const results: NoteGroup[] = [];
    if (today.length) {
      results.push({ label: 'Today', notes: today });
    }
    if (previous7.length) {
      results.push({ label: 'This Week', notes: previous7 });
    }
    const sortedMonths = Array.from(monthBuckets.entries()).sort((a, b) => b[0] - a[0]);
    sortedMonths.forEach(([, bucket]) => {
      results.push({ label: bucket.label, notes: bucket.notes });
    });
    const sortedPastYears = Array.from(pastYears.entries()).sort(
      (a, b) => b[0] - a[0],
    );
    sortedPastYears.forEach(([year, yearNotes]) => {
      results.push({ label: `${year}`, notes: yearNotes });
    });

    return results;
  }, [filteredNotes, timezone]);

  const selectedNote = useMemo(
    () => notes.find((note) => note.id === selectedNoteId) ?? null,
    [notes, selectedNoteId],
  );

  const flushSave = useCallback(async () => {
    const pending = pendingSaveRef.current;
    if (!pending) {
      return;
    }

    pendingSaveRef.current = null;
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }

    // Drafts typed after this save was queued have a newer revision and are kept until their own save lands.
    const savedRevision = localDraftsRef.current[pending.noteId]?.revision;
    const runSave = async () => {
      if (deletedNoteIds.has(pending.noteId)) {
        return;
      }
      setIsSaving(true);
      try {
        const saved = await updateNote(pending.noteId, pending.updates);
        if (saved && localDraftsRef.current[pending.noteId]?.revision === savedRevision) {
          delete localDraftsRef.current[pending.noteId];
        }
      } catch (err) {
        // Error state handled in hook; keep UI responsive
      } finally {
        setIsSaving(false);
      }
    };
    const queued = (noteSaveQueues.get(pending.noteId) ?? Promise.resolve()).then(runSave, runSave);
    noteSaveQueues.set(pending.noteId, queued);
    await queued;
    if (noteSaveQueues.get(pending.noteId) === queued) {
      noteSaveQueues.delete(pending.noteId);
    }
  }, [updateNote]);

  const queueSave = useCallback(
    (updates: Partial<Pick<ToolkitNote, 'title' | 'body'>>) => {
      if (!selectedNoteId) {
        return;
      }

      const existing = pendingSaveRef.current;
      pendingSaveRef.current = {
        noteId: selectedNoteId,
        updates: { ...(existing?.updates ?? {}), ...updates },
      };

      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }

      saveTimeoutRef.current = setTimeout(() => {
        void flushSave();
      }, DEFAULT_AUTOSAVE_DELAY);
    },
    [flushSave, selectedNoteId],
  );

  const handleSelectNote = useCallback(
    (noteId: string, track = true) => {
      if (noteId === selectedNoteId) {
        return;
      }

      void flushSave();
      selectNote(noteId, { trackEvent: track });
    },
    [flushSave, selectNote, selectedNoteId],
  );

  const handleCreateNote = useCallback(async () => {
    await flushSave();
    const newNote = await createNote();
    if (newNote) {
      setEditorState({ title: editableTitle(newNote.title), body: newNote.body });
      handleSelectNote(newNote.id);
    }
  }, [createNote, flushSave, handleSelectNote]);

  const handleDeleteNote = useCallback(async () => {
    if (!selectedNote) {
      return;
    }
    const noteId = selectedNote.id;
    void flushSave();
    if (await deleteNote(noteId)) {
      delete localDraftsRef.current[noteId];
      // Saves still queued for the deleted note skip themselves; drop the marker once they have drained.
      deletedNoteIds.add(noteId);
      void (noteSaveQueues.get(noteId) ?? Promise.resolve()).then(() => deletedNoteIds.delete(noteId));
    }
  }, [deleteNote, flushSave, selectedNote]);

  const rememberLocalDraft = useCallback(
    (updates: Partial<{ title: string; body: string }>) => {
      if (selectedNoteId) {
        const revision = (localDraftsRef.current[selectedNoteId]?.revision ?? 0) + 1;
        localDraftsRef.current[selectedNoteId] = { ...editorStateRef.current, ...updates, revision };
      }
    },
    [selectedNoteId],
  );

  const handleTitleChange = useCallback(
    (value: string) => {
      setEditorState((prev) => ({ ...prev, title: value }));
      rememberLocalDraft({ title: value });
      queueSave({ title: value });
    },
    [queueSave, rememberLocalDraft],
  );

  const handleBodyChange = useCallback(
    (value: string) => {
      setEditorState((prev) => ({ ...prev, body: value }));
      rememberLocalDraft({ body: value });
      queueSave({ body: value });
    },
    [queueSave, rememberLocalDraft],
  );

  useEffect(() => {
    if (!selectedNoteId && groupedNotes.length) {
      const firstNote = groupedNotes[0]?.notes[0];
      if (firstNote) {
        handleSelectNote(firstNote.id, false);
      }
    }
  }, [groupedNotes, handleSelectNote, selectedNoteId]);

  useEffect(() => {
    // Seed the editor only when a different note is selected. Save responses for the note being
    // edited must not overwrite newer keystrokes or cancel their queued save.
    const nextNoteId = selectedNote?.id ?? null;
    if (seededNoteIdRef.current === nextNoteId) {
      return;
    }
    seededNoteIdRef.current = nextNoteId;
    const localDraft = nextNoteId ? localDraftsRef.current[nextNoteId] : undefined;
    if (selectedNote) {
      setEditorState(localDraft
        ? { title: localDraft.title, body: localDraft.body }
        : { title: editableTitle(selectedNote.title), body: selectedNote.body });
    } else {
      setEditorState({ title: '', body: '' });
    }
    pendingSaveRef.current = null;
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }
  }, [selectedNote]);

  useEffect(() => {
    return () => {
      void flushSave();
    };
  }, [flushSave]);

  return (
    <Card
      maxWidth="none"
      padding="large"
      className="flex h-auto min-w-0 max-w-full flex-col gap-6 p-4 sm:p-6 md:h-[calc(100vh-130px)] md:flex-row md:gap-6 md:p-10"
    >
      <aside className="flex h-auto min-h-0 w-full min-w-0 flex-col items-start gap-6 overflow-x-hidden px-0 pb-2 pt-0 md:h-full md:max-w-[324px] md:basis-[324px] md:grow-0 md:shrink-0 md:pb-4">
        <div className="flex items-center justify-between w-full">
          <h1 className="font-headings-h3 text-[length:var(--headings-h3-font-size)] tracking-[var(--headings-h3-letter-spacing)] leading-[var(--headings-h3-line-height)] font-[number:var(--headings-h3-font-weight)] text-foundation-base-darkbase-dark-500 [font-style:var(--headings-h3-font-style)]">
            Notes
          </h1>

            <Button
            variant="ghost"
            size="icon"
            className="p-0 hover:text-foundation-base-darkbase-dark-700 h-auto w-auto"
            onClick={handleCreateNote}
          >
            <PlusIcon className="min-w-7 min-h-7" />
          </Button>
        </div>

        <div className="relative w-full">
          <Input
            type="text"
            placeholder="Search notes..."
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
          />
          <SearchIcon className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-foundation-base-darkbase-dark-200 pointer-events-none" />
        </div>

        {error && (
          <div className="w-full rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {error}
          </div>
        )}

        <ScrollArea className="h-[38vh] w-full min-w-0 overflow-x-hidden touch-pan-y md:h-full md:max-w-[324px]">
          <div className="w-full min-w-0 overflow-x-hidden pr-0 md:max-w-[324px] md:pr-4">
            {loading && (
              <p className="text-sm text-foundation-base-darkbase-dark-200">
                Loading notes...
              </p>
            )}

            {!loading && !groupedNotes.length && (
                <p className="text-sm text-foundation-base-darkbase-dark-300">
                  You don't have any notes yet.
                </p>
            )}

            {groupedNotes.map((group) => (
              <section
                key={group.label}
                className="flex min-w-0 max-w-full overflow-hidden flex-col items-start gap-6 w-full mb-6"
              >
                <h2 className="type-body-s-semibold text-foundation-base-darkbase-dark-700">
                  {group.label}
                </h2>

                <div className="flex min-w-0 max-w-full overflow-hidden flex-col items-start gap-4 w-full">
                  {group.notes.map((note) => (
                    <ToolkitItem
                      key={note.id}
                      note={note}
                      timezone={timezone}
                      isActive={selectedNoteId === note.id}
                      onSelect={(id) => void handleSelectNote(id)}
                    />
                  ))}
                </div>
              </section>
            ))}
          </div>
        </ScrollArea>
      </aside>

      <ToolkitMainSection
        note={selectedNote}
        editorState={editorState}
        timezone={timezone}
        isSaving={isSaving}
        onTitleChange={handleTitleChange}
        onBodyChange={handleBodyChange}
        onDelete={handleDeleteNote}
        onCreate={handleCreateNote}
      />
    </Card>
  );
};

const ToolkitItem: React.FC<ToolkitItemProps> = ({ note, isActive, timezone, onSelect }) => {
  const subtitle = formatListDate(note, timezone);

  return (
    <Card
      className={`w-full min-w-0 overflow-hidden cursor-pointer transition-colors hover:bg-white md:max-w-[324px] ${
        isActive ? 'bg-white' : 'bg-white/40'
      } rounded-2xl border-0 shadow-none`}
      onClick={() => onSelect(note.id)}
      padding="none"
      maxWidth="none"
    >
      <CardContent className="flex min-w-0 max-w-full w-full flex-col items-start gap-1 p-4">
        <p className="w-full whitespace-normal break-words type-body-m-semibold text-foundation-base-darkbase-dark-300 text-[length:var(--body-s-semibold-font-size)]">
          {note.title}
        </p>
        <p className="font-body-s text-foundation-base-darkbase-dark-300 text-[length:var(--body-XS-font-size)] tracking-[var(--body-s-letter-spacing)] leading-[var(--body-s-line-height)] [font-style:var(--body-s-font-style)]">
          {subtitle}
        </p>
      </CardContent>
    </Card>
  );
};

const ToolkitMainSection: React.FC<ToolkitMainSectionProps> = ({
  note,
  editorState,
  isSaving,
  onTitleChange,
  onBodyChange,
  onDelete,
  onCreate,
}) => {
  if (!note) {
    return (
      <section className="flex flex-1 self-stretch items-stretch gap-4 md:gap-10">
        <Card maxWidth="none" padding="large" className="flex flex-col items-center justify-center text-center gap-4 flex-1">
          <h2 className="font-headings-h4 text-foundation-base-darkbase-dark-500">
            Create your first note
          </h2>
          <p className="text-sm text-foundation-base-darkbase-dark-200 max-w-md">
            Capture ideas, references, and reflections in one place.
          </p>
          <Button onClick={onCreate} size='auto'>
            <PlusIcon className="mr-2 h-4 w-4" />
            New note
          </Button>
        </Card>
      </section>
    );
  }

  return (
    <section className="flex flex-1 self-stretch items-stretch gap-4 md:gap-10">
      <Card maxWidth="none" padding="large" className="flex flex-col">
        <div className="flex flex-col flex-1 gap-6 min-h-0">
          <ScrollArea className="flex-1 -mx-10 px-10">
            <div className="flex flex-col gap-6 pr-4">
              <div className="flex items-start justify-between w-full gap-4">
                <TextareaAutosize
                  value={editorState.title}
                  onChange={(event) => onTitleChange(event.target.value)}
                  className="font-headings-h3 font-[number:var(--headings-h3-font-weight)] text-foundation-base-darkbase-dark-500 text-xl md:text-[length:var(--headings-h3-font-size)] tracking-[var(--headings-h3-letter-spacing)] leading-tight md:leading-[var(--headings-h3-line-height)] [font-style:var(--headings-h3-font-style)] border-0 outline-none resize-none bg-transparent w-full focus:ring-0 whitespace-pre-wrap overflow-hidden"
                  placeholder={UNTITLED_NOTE}
                  aria-label="Note title"
                />

                <Button
                  variant="ghost"
                  size="icon"
                  className="h-auto w-auto p-0 hover:bg-transparent flex-shrink-0"
                  onClick={onDelete}
                  title="Delete note"
                >
                  <Trash2Icon className="w-6 h-6" />
                </Button>
              </div>

              <TextareaAutosize
                value={editorState.body}
                onChange={(event) => onBodyChange(event.target.value)}
                className="type-body-m text-foundation-base-darkbase-dark-300 border-0 outline-none resize-none bg-transparent w-full focus:ring-0"
                placeholder="Write your thoughts here..."
              />
            </div>
          </ScrollArea>

          {(note.source_flow_name || isSaving) && (
            <div className="flex items-center justify-between text-foundation-base-darkbase-dark-300 text-sm">
              {note.source_flow_name ? (
                <p>
                  {note.source_flow_name}
                </p>
              ) : (
                <span />
              )}
              {isSaving && (
                <span className="inline-flex items-center gap-2 text-xs text-foundation-base-darkbase-dark-200">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Saving...
                </span>
              )}
            </div>
          )}
        </div>
      </Card>
    </section>
  );
};

